import * as React from "react";
import AppIcon from "../components/AppIcon";
import axios from "axios";
import { Attendee, useUser } from "../utils/AccountStore";
import { Exhibitor } from "./Exhibitors";
import { useNavigate, useParams } from "react-router-dom";

export const GetExhibitor = async (id: string) => {
    return axios.get(`${Server}/api/exhibitors/${id}`);
};

const ExhibitorDetails = () => {
    const { id } = useParams();
    const [exhibitor, setExhibitor] = React.useState({} as Exhibitor);
    const { user, getUser } = useUser();
    const [isFavorite, setIsFavorite] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [imageError, setImageError] = React.useState(false);

    const navigate = useNavigate();

    React.useEffect(() => {
        if (id) {
            GetExhibitor(id)
                .then((res) => {
                    if (res.status === 200) {
                        setExhibitor(res.data);
                        setLoading(false);
                    } else {
                        alert("Error getting exhibitor");
                        navigate("/home");
                    }
                })
                .catch(() => {
                    alert("Error getting exhibitor");
                    navigate("/home");
                });
        }
    }, [id, navigate]);

    React.useEffect(() => {
        if (user.id && exhibitor.id) {
            setIsFavorite(user.favorites.includes(exhibitor.id));
        }
    }, [user, exhibitor]);

    const toggleFavorite = async () => {
        await axios.post(`${Server}/api/attendees/toggleFavorite`, {
            userID: user.id,
            exhibitorID: exhibitor.id,
        });
        getUser();
    };

    const handleImageError = () => {
        setImageError(true);
    };

    const handleImageLoad = () => {
        setImageError(false);
    };

    return (
        <div
            className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
            style={{
                backgroundImage: "url(/images/collectivebg-2.jpg)",
            }}
        >
            <div className="min-h-screen backdrop-blur-sm" style={{ backgroundColor: "rgba(241, 245, 248, 0.9)" }}>
                {/* Header Section */}
                <div className="pt-8 pb-6">
                    <div className="max-w-4xl mx-auto px-8">
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigate("/exhibitors")}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                                style={{ backgroundColor: "rgba(121, 153, 186, 0.9)" }}
                            >
                                <span className="text-white text-xl">←</span>
                            </button>

                            <button
                                onClick={() => navigate("/home")}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                                style={{ backgroundColor: "rgba(112, 131, 114, 0.9)" }}
                            >
                                <span className="text-white text-xl"><AppIcon kind="home" /></span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="px-8 pb-8">
                    <div className="max-w-4xl mx-auto">
                        {loading ? (
                            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-12 shadow-lg text-center" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                <div className="mb-4">
                                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent mx-auto" style={{ borderTopColor: "#DB1C7D" }}></div>
                                </div>
                                <h3 className="text-xl font-bold" style={{ color: "#013A51" }}>
                                    Loading Vendor Details...
                                </h3>
                            </div>
                        ) : !exhibitor.id ? (
                            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-12 shadow-lg text-center" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                <div className="mb-4">
                                    <span className="text-6xl"><AppIcon kind="times" /></span>
                                </div>
                                <h3 className="text-2xl font-bold mb-2" style={{ color: "#013A51" }}>
                                    Vendor Not Found
                                </h3>
                                <p className="text-lg" style={{ color: "#708372" }}>
                                    The vendor you're looking for doesn't exist or has been removed.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {/* Vendor Header Card */}
                                <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 shadow-lg" style={{ borderLeft: `4px solid #DB1C7D` }}>
                                    <div className="flex flex-col md:flex-row items-center gap-8">
                                        {/* Vendor Logo */}
                                        <div className="relative flex-shrink-0">
                                            <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-lg bg-white p-4">
                                                {!exhibitor.logo || imageError ? (
                                                    <div className="w-full h-full flex items-center justify-center text-4xl" style={{ backgroundColor: "rgba(219, 28, 125, 0.1)", color: "#DB1C7D" }}>
                                                        <AppIcon kind="store" />
                                                    </div>
                                                ) : (
                                                    <img 
                                                        src={exhibitor.logo} 
                                                        alt={`${exhibitor.name} logo`} 
                                                        className="w-full h-full object-contain" 
                                                        onError={handleImageError}
                                                        onLoad={handleImageLoad}
                                                    />
                                                )}
                                            </div>
                                            {exhibitor.isTreasure && (
                                                <div
                                                    className="absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center text-lg shadow-lg"
                                                    style={{ backgroundColor: "#DDB649" }}
                                                >
                                                    <AppIcon kind="gem" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Vendor Info */}
                                        <div className="flex-1 text-center md:text-left">
                                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                                <div>
                                                    <h1 className="text-3xl font-bold mb-2" style={{ color: "#013A51" }}>
                                                        {exhibitor.name}
                                                    </h1>
                                                    <div className="w-16 h-1 mx-auto md:mx-0 rounded-full mb-3" style={{ backgroundColor: "#DB1C7D" }}></div>

                                                    {exhibitor.location && (
                                                        <p className="text-lg mb-2 flex items-center justify-center md:justify-start gap-2" style={{ color: "#708372" }}>
                                                            <span><AppIcon kind="marker" /></span>
                                                            <span>{exhibitor.location}</span>
                                                        </p>
                                                    )}

                                                    {exhibitor.website && (
                                                        <a
                                                            href={exhibitor.website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-2 text-lg font-semibold transition-colors duration-200 hover:opacity-80"
                                                            style={{ color: "#DB1C7D" }}
                                                        >
                                                            <span><AppIcon kind="globe" /></span>
                                                            <span>Visit Website</span>
                                                        </a>
                                                    )}
                                                </div>

                                                {/* Favorite Button */}
                                                <button
                                                    onClick={toggleFavorite}
                                                    aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                                                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:shadow-lg transform hover:scale-110"
                                                    style={{
                                                        backgroundColor: isFavorite ? "rgba(219, 28, 125, 0.9)" : "rgba(112, 131, 114, 0.6)",
                                                    }}
                                                >
                                                    <span className="text-xl text-white"><AppIcon kind={isFavorite ? "heart" : "heartOutline"} /></span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Vendor Description */}
                                {exhibitor.description && (
                                    <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 shadow-lg" style={{ borderLeft: `4px solid #7999BA` }}>
                                        <h2 className="text-2xl font-bold mb-4" style={{ color: "#013A51" }}>
                                            About {exhibitor.name}
                                        </h2>
                                        <div
                                            className="text-lg leading-relaxed prose prose-lg max-w-none"
                                            style={{ color: "#8B6F4C" }}
                                            dangerouslySetInnerHTML={{ __html: exhibitor.description.replace(/\n/g, "<br />") }}
                                        />
                                    </div>
                                )}

                                {/* Special Features */}
                                {exhibitor.isTreasure && (
                                    <div className="bg-gradient-to-r from-yellow-100 to-yellow-50 bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 shadow-lg" style={{ borderLeft: `4px solid #DDB649` }}>
                                        <div className="flex items-center gap-4 mb-4">
                                            <span className="text-3xl"><AppIcon kind="gem" /></span>
                                            <h2 className="text-2xl font-bold" style={{ color: "#013A51" }}>
                                                Featured Artisan
                                            </h2>
                                        </div>
                                        <p className="text-lg" style={{ color: "#8B6F4C" }}>
                                            This vendor is part of our special featured artisan collection! Don't miss the opportunity to discover their unique crafts and creations.
                                        </p>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <button
                                        onClick={() => navigate("/exhibitors")}
                                        className="w-full px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg backdrop-blur-sm text-white"
                                        style={{ backgroundColor: "rgba(121, 153, 186, 0.9)" }}
                                    >
                                        ← Browse More Vendors
                                    </button>

                                    <button
                                        onClick={() => navigate("/floorPlans")}
                                        className="w-full px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg backdrop-blur-sm text-white"
                                        style={{ backgroundColor: "rgba(219, 28, 125, 0.9)" }}
                                    >
                                        <AppIcon kind="map" /> Find on Map
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExhibitorDetails;
